import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  FormControl,
  InputLabel,
  Select,
  TextField,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Snackbar,
} from '@mui/material';
import {
  Add,
  MoreVert,
  Edit,
  Delete,
  NotificationsActive,
  Send,
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
} from '@mui/icons-material';
import {
  useAlertConfigs,
  useAlertHistory,
  useCreateAlertConfig,
  useUpdateAlertConfig,
  useDeleteAlertConfig,
  useTestAlertWebhook,
  useOrganizations,
} from '../../hooks/useFleet';
import type { AlertConfig } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { FLEET_TABS, FLEET_ACCENT_COLOR } from './fleetTabs';
import { fleetHelp } from '../../components/PageHelp/helpContent';

interface AlertFormData {
  organization_id: string;
  name: string;
  enabled: boolean;
  event_types: string[];
  severity_threshold: string;
  cooldown_minutes: number;
  channel_type: 'slack' | 'google_chat';
  webhook_url: string;
}

const initialFormData: AlertFormData = {
  organization_id: '',
  name: '',
  enabled: true,
  event_types: ['offline'],
  severity_threshold: 'warning',
  cooldown_minutes: 15,
  channel_type: 'slack',
  webhook_url: '',
};

const eventTypeOptions = [
  { value: 'offline', label: 'Device Offline' },
  { value: 'online', label: 'Device Online' },
  { value: 'error', label: 'Device Error' },
  { value: 'config_change', label: 'Config Change' },
  { value: 'firmware_update', label: 'Firmware Update' },
];

const severityOptions = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
];

const AlertsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<AlertConfig | null>(null);
  const [formData, setFormData] = useState<AlertFormData>(initialFormData);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConfig, setSelectedConfig] = useState<AlertConfig | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: configs, isLoading, error } = useAlertConfigs();
  const { data: history } = useAlertHistory();
  const { data: orgsData } = useOrganizations();

  const createMutation = useCreateAlertConfig();
  const updateMutation = useUpdateAlertConfig();
  const deleteMutation = useDeleteAlertConfig();
  const testMutation = useTestAlertWebhook();

  const organizations = orgsData?.data || [];

  const handleOpenDialog = (config?: AlertConfig) => {
    if (config) {
      setEditingConfig(config);
      // event_types comes as JSON string from API, parse it to array
      const eventTypes = typeof config.event_types === 'string'
        ? JSON.parse(config.event_types || '["offline"]')
        : config.event_types || ['offline'];
      setFormData({
        organization_id: config.organization_id,
        name: config.name,
        enabled: config.enabled,
        event_types: eventTypes,
        severity_threshold: config.severity_threshold,
        cooldown_minutes: config.cooldown_minutes,
        channel_type: config.channel_type as 'slack' | 'google_chat',
        webhook_url: config.webhook_url,
      });
    } else {
      setEditingConfig(null);
      setFormData({
        ...initialFormData,
        organization_id: organizations[0]?.id || '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async () => {
    try {
      // API expects event_types as array (will be JSON stringified on server if needed)
      const submitData = {
        ...formData,
      };

      if (editingConfig) {
        await updateMutation.mutateAsync({
          id: editingConfig.id,
          data: submitData,
        });
        setSnackbar({ open: true, message: 'Alert configuration updated', severity: 'success' });
      } else {
        await createMutation.mutateAsync(submitData);
        setSnackbar({ open: true, message: 'Alert configuration created', severity: 'success' });
      }
      handleCloseDialog();
    } catch (err) {
      console.error('Failed to save alert config:', err);
      setSnackbar({ open: true, message: 'Failed to save configuration', severity: 'error' });
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, config: AlertConfig) => {
    setAnchorEl(event.currentTarget);
    setSelectedConfig(config);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConfig(null);
  };

  const handleEditClick = () => {
    if (selectedConfig) {
      handleOpenDialog(selectedConfig);
    }
    handleMenuClose();
  };

  const handleTestClick = async () => {
    if (selectedConfig) {
      try {
        await testMutation.mutateAsync(selectedConfig.id);
        setSnackbar({ open: true, message: 'Test alert sent successfully', severity: 'success' });
      } catch (err) {
        setSnackbar({ open: true, message: 'Failed to send test alert', severity: 'error' });
      }
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedConfig) {
      try {
        await deleteMutation.mutateAsync(selectedConfig.id);
        setSnackbar({ open: true, message: 'Alert configuration deleted', severity: 'success' });
      } catch (err) {
        console.error('Failed to delete alert config:', err);
        setSnackbar({ open: true, message: 'Failed to delete configuration', severity: 'error' });
      }
    }
    setDeleteConfirmOpen(false);
    setSelectedConfig(null);
  };

  const getOrgName = (orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    return org?.name || 'Unknown';
  };

  const getChannelIcon = (type: string) => {
    if (type === 'slack') {
      return (
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            bgcolor: '#4A154B',
            color: 'white',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: '0.75rem',
            fontWeight: 600,
          }}
        >
          Slack
        </Box>
      );
    }
    return (
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          bgcolor: '#1a73e8',
          color: 'white',
          px: 1,
          py: 0.25,
          borderRadius: 1,
          fontSize: '0.75rem',
          fontWeight: 600,
        }}
      >
        Google Chat
      </Box>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <SectionTabs
        title="Fleet Management"
        tabs={FLEET_TABS}
        accentColor={FLEET_ACCENT_COLOR}
        help={fleetHelp.alerts}
        actionButton={{
          label: 'New Alert',
          icon: <Add />,
          onClick: () => handleOpenDialog(),
        }}
      />

      {organizations.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Create an organization first before configuring alerts.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load alert configurations. Please try again.
        </Alert>
      )}

      <Paper sx={{ bgcolor: '#1e1e1e', borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: '1px solid #333', px: 2 }}
        >
          <Tab label="Configurations" />
          <Tab label="History" />
        </Tabs>

        {tabValue === 0 && (
          <>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#6366f1' }} />
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Organization</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Channel</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Events</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!configs || configs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <NotificationsActive sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                          <Typography color="text.secondary">
                            No alert configurations. Create one to start receiving notifications.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      configs.map((config) => (
                        <TableRow key={config.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <NotificationsActive sx={{ color: '#6366f1' }} />
                              <Typography fontWeight={500}>{config.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={getOrgName(config.organization_id)} size="small" />
                          </TableCell>
                          <TableCell>{getChannelIcon(config.channel_type)}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {(typeof config.event_types === 'string'
                                ? JSON.parse(config.event_types || '[]')
                                : config.event_types || []
                              ).map((event: string) => (
                                <Chip key={event} label={event} size="small" variant="outlined" />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={config.enabled ? 'Active' : 'Disabled'}
                              size="small"
                              color={config.enabled ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={(e) => handleMenuOpen(e, config)}>
                              <MoreVert />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {tabValue === 1 && (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Alert</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!history || history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Schedule sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                      <Typography color="text.secondary">
                        No alert history yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(item.created_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.alert_config_id}</TableCell>
                      <TableCell>{item.device_id || '-'}</TableCell>
                      <TableCell>
                        {item.status === 'sent' ? (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 16 }} />}
                            label="Sent"
                            size="small"
                            color="success"
                          />
                        ) : item.status === 'failed' ? (
                          <Chip
                            icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                            label="Failed"
                            size="small"
                            color="error"
                          />
                        ) : (
                          <Chip label={item.status} size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleTestClick}>
          <ListItemIcon>
            <Send fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send Test</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingConfig ? 'Edit Alert Configuration' : 'Create Alert Configuration'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Organization</InputLabel>
              <Select
                value={formData.organization_id}
                label="Organization"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, organization_id: e.target.value as string }))
                }
              >
                {organizations.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Configuration Name"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
              placeholder="e.g., Device Offline Alerts"
            />

            <FormControl fullWidth required>
              <InputLabel>Channel Type</InputLabel>
              <Select
                value={formData.channel_type}
                label="Channel Type"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    channel_type: e.target.value as 'slack' | 'google_chat',
                  }))
                }
              >
                <MenuItem value="slack">Slack</MenuItem>
                <MenuItem value="google_chat">Google Chat</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Webhook URL"
              value={formData.webhook_url}
              onChange={(e) => setFormData((prev) => ({ ...prev, webhook_url: e.target.value }))}
              fullWidth
              required
              placeholder={
                formData.channel_type === 'slack'
                  ? 'https://hooks.slack.com/services/...'
                  : 'https://chat.googleapis.com/v1/spaces/...'
              }
              helperText={
                formData.channel_type === 'slack'
                  ? 'Get this from Slack App > Incoming Webhooks'
                  : 'Get this from Google Chat > Webhooks'
              }
            />

            <FormControl fullWidth>
              <InputLabel>Event Types</InputLabel>
              <Select
                multiple
                value={formData.event_types}
                label="Event Types"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, event_types: e.target.value as string[] }))
                }
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {eventTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Severity Threshold</InputLabel>
              <Select
                value={formData.severity_threshold}
                label="Severity Threshold"
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, severity_threshold: e.target.value }))
                }
              >
                {severityOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Cooldown (minutes)"
              type="number"
              value={formData.cooldown_minutes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 15 }))
              }
              fullWidth
              helperText="Minimum time between alerts for the same device"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => setFormData((prev) => ({ ...prev, enabled: e.target.checked }))}
                />
              }
              label="Enabled"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.organization_id ||
              !formData.name ||
              !formData.webhook_url ||
              createMutation.isPending ||
              updateMutation.isPending
            }
            sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : editingConfig ? (
              'Save Changes'
            ) : (
              'Create Configuration'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Alert Configuration?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedConfig?.name}</strong>? You will stop
            receiving alerts from this configuration.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AlertsPage;
